/** 商品上架页扩展字段（侵权风险、刊登状态等），本地持久化；后续可换后端接口 */
import { userLocal } from '@/utils/ipeUserLocalState';

const STORAGE_KEY = 'ipe-platform-pod-product-meta-v1';

function readRawFromItem(row) {
  if (!row || typeof row !== 'object') return {};
  if (row.raw && typeof row.raw === 'object') return row.raw;
  const meta = row.productMetadata && typeof row.productMetadata === 'object'
    ? row.productMetadata
    : null;
  try {
    const parsed = JSON.parse(String(row.rawJsonSnippet || row.raw_json_snippet || ''));
    if (parsed && typeof parsed === 'object') {
      if (meta?.preferredProductVideo && !parsed.preferredProductVideo) {
        return { ...parsed, preferredProductVideo: meta.preferredProductVideo };
      }
      return parsed;
    }
  } catch {
    // snippet 可能被截断
  }
  if (meta?.preferredProductVideo) {
    return { preferredProductVideo: meta.preferredProductVideo };
  }
  return {};
}

/**
 * 将后端 raw 中的侵权检测结果同步到本地 meta（供详情表展示）。
 * @returns {boolean} 是否有写入
 */
export function hydrateProductMetaFromItemRaw(row) {
  const id = row?.id;
  if (id == null) return false;
  const raw = readRawFromItem(row);
  let changed = false;

  const video = raw?.preferredProductVideo;
  if (video && typeof video === 'object') {
    const current = getProductMeta(id);
    const hasLocal = current.preferredProductVideo
      && typeof current.preferredProductVideo === 'object'
      && String(current.preferredProductVideo.url || current.preferredProductVideo.ipeUrl || '').trim();
    const remoteUrl = String(video.url || video.ipeUrl || video.videoUrl || '').trim();
    if (remoteUrl && !hasLocal) {
      setProductMeta(id, { preferredProductVideo: video });
      changed = true;
    }
  }

  const extraRisk = String(raw?.risk || '').trim().toLowerCase();
  if (!extraRisk || extraRisk === 'pending') return changed;
  const current = getProductMeta(id);
  const backendAt = String(raw?.assessedAt || '').trim();
  const localAt = String(current?.assessedAt || '').trim();
  if (current.risk && current.risk !== 'pending' && localAt && (!backendAt || localAt >= backendAt)) {
    return changed;
  }
  const elements = Array.isArray(raw?.riskElements)
    ? raw.riskElements.map((el) => String(el || '').trim()).filter(Boolean)
    : [];
  setProductMeta(id, {
    risk: extraRisk,
    assessing: false,
    riskAdvice: raw?.riskAdvice || (elements.length ? elements.join('、') : ''),
    infringementBatchId: raw?.infringementBatchId,
    assessedAt: backendAt || new Date().toISOString(),
  });
  return true;
}

export function readMetaMap() {
  try {
    const o = userLocal.getJson(STORAGE_KEY, {});
    return typeof o === 'object' && o && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
}

function writeMetaMap(map) {
  userLocal.setJson(STORAGE_KEY, map);
}

/** @returns {{ risk: string, listed: boolean, listingJobPending: boolean, listingJobId: number|null, assessing: boolean, riskAdvice: string, listingShopId: string, listingShopName: string, listingCategoryPathLabel: string, listingProductTemplateId: string, listingProductTemplateName: string }} */
export function getProductMeta(id) {
  const row = readMetaMap()[String(id)] || {};
  return {
    risk: typeof row.risk === 'string' ? row.risk : 'pending',
    listed: Boolean(row.listed),
    listingJobPending: Boolean(row.listingJobPending),
    listingJobId: row.listingJobId != null ? Number(row.listingJobId) : null,
    assessing: Boolean(row.assessing),
    riskAdvice: typeof row.riskAdvice === 'string' ? row.riskAdvice : '',
    listingShopId: typeof row.listingShopId === 'string' ? row.listingShopId : '',
    listingShopName: typeof row.listingShopName === 'string' ? row.listingShopName : '',
    listingCategoryPathLabel: typeof row.listingCategoryPathLabel === 'string' ? row.listingCategoryPathLabel : '',
    listingProductTemplateId: typeof row.listingProductTemplateId === 'string' ? row.listingProductTemplateId : '',
    listingProductTemplateName: typeof row.listingProductTemplateName === 'string' ? row.listingProductTemplateName : '',
    listingFailed: Boolean(row.listingFailed),
    listingLog: typeof row.listingLog === 'string' ? row.listingLog : '',
    listingPlatformSpu: typeof row.listingPlatformSpu === 'string' ? row.listingPlatformSpu : '',
    listingPlatformSkcs: Array.isArray(row.listingPlatformSkcs)
      ? row.listingPlatformSkcs.map((id) => String(id || '').trim()).filter(Boolean)
      : [],
    listingError: typeof row.listingError === 'string' ? row.listingError : '',
    /** 本次刊登实际覆盖的颜色身份键；null 表示全色刊登 */
    publishColorKeys: Array.isArray(row.publishColorKeys)
      ? row.publishColorKeys.map((key) => String(key || '').trim()).filter(Boolean)
      : null,
    /** 复色成功后待合并进 publishColorKeys 的颜色键 */
    pendingRecolorColorKeys: Array.isArray(row.pendingRecolorColorKeys)
      ? row.pendingRecolorColorKeys.map((key) => String(key || '').trim()).filter(Boolean)
      : [],
    assessedAt: typeof row.assessedAt === 'string' ? row.assessedAt : '',
    infringementBatchId: row.infringementBatchId != null ? row.infringementBatchId : null,
    preferredProductVideo: row.preferredProductVideo && typeof row.preferredProductVideo === 'object'
      ? row.preferredProductVideo
      : null,
  };
}

export function replaceProductMeta(id, next) {
  const map = { ...readMetaMap() };
  const key = String(id ?? '').trim();
  if (!key) return;
  if (!next || typeof next !== 'object') {
    delete map[key];
  } else {
    const copy = { ...next };
    delete copy.tags;
    map[key] = copy;
  }
  writeMetaMap(map);
  window.dispatchEvent(new CustomEvent('ipe-platform-pod-meta-changed'));
}

export function setProductMeta(id, patch) {
  const map = { ...readMetaMap() };
  const cur = map[String(id)] && typeof map[String(id)] === 'object' ? { ...map[String(id)] } : {};
  const next = { ...cur, ...patch };
  // 商品标签字段已下线，写入时清掉历史 tags
  delete next.tags;
  map[String(id)] = next;
  writeMetaMap(map);
  window.dispatchEvent(new CustomEvent('ipe-platform-pod-meta-changed'));
}

function extractColorSlotsArrayField(row) {
  if (!row || typeof row !== 'object') return [];
  const direct = row.colorSlots ?? row.color_slots;
  if (Array.isArray(direct) && direct.length) return direct;
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : null;
  if (raw) {
    const fromRaw = raw.colorSlots ?? raw.color_slots;
    if (Array.isArray(fromRaw) && fromRaw.length) return fromRaw;
  }
  const snippet = String(row.rawJsonSnippet || row.raw_json_snippet || '').trim();
  if (!snippet) return [];
  try {
    const parsed = JSON.parse(snippet);
    const fromSnippet = parsed?.colorSlots ?? parsed?.color_slots;
    if (Array.isArray(fromSnippet) && fromSnippet.length) return fromSnippet;
  } catch {
    // snippet 可能被截断
  }
  return [];
}

/** 单个颜色槽上的细节图 URL 列表 */
export function detailUrlsFromListingColorSlot(slot) {
  if (!slot || typeof slot !== 'object') return [];
  if (Array.isArray(slot.detailUrls) && slot.detailUrls.length) {
    return slot.detailUrls.map((url) => String(url || '').trim()).filter(Boolean);
  }
  if (Array.isArray(slot.detail_urls) && slot.detail_urls.length) {
    return slot.detail_urls.map((url) => String(url || '').trim()).filter(Boolean);
  }
  return [];
}

/** 多个颜色槽 → 扁平细节图 URL 列表 */
export function flatDetailUrlsFromColorSlots(slots) {
  return (Array.isArray(slots) ? slots : []).flatMap((slot) => detailUrlsFromListingColorSlot(slot));
}

/**
 * 单色刊登：行上临时挂载 `ipePublishColorIndexes`（对应 resolveListingColorSlots 的下标）
 * 时只保留这些颜色，用于「同一个 SPU 只刊登其中某个颜色」。
 * 未挂载该字段时行为不变（全色刊登）。
 */
export const LISTING_PUBLISH_COLOR_INDEXES_FIELD = 'ipePublishColorIndexes';

function resolveListingColorSlotsRaw(row) {
  const id = String(row?.id ?? '').trim();
  if (id) {
    const meta = readMetaMap()[id];
    if (Array.isArray(meta?.preferredColorSlots) && meta.preferredColorSlots.length) {
      return meta.preferredColorSlots;
    }
  }
  const slots = extractColorSlotsArrayField(row);
  if (!id || !slots.length) return slots;
  // 无 preferredColorSlots 时，仍可用扁平 preferredColor* 覆盖首槽颜色
  const meta = readMetaMap()[id];
  const label = String(meta?.preferredColorLabel || '').trim();
  const code = String(meta?.preferredColorCode || '').trim();
  if (!label && !code) return slots;
  return slots.map((slot, index) => {
    if (index !== 0 || !slot || typeof slot !== 'object') return slot;
    return {
      ...slot,
      colorLabel: label || slot.colorLabel || slot.color_label || '',
      color_label: label || slot.color_label || slot.colorLabel || '',
      colorCode: code || slot.colorCode || slot.color_code || '',
      color_code: code || slot.color_code || slot.colorCode || '',
    };
  });
}

/** 颜色槽：优先读手动编辑写入的 preferredColorSlots，再回退行上 colorSlots */
export function resolveListingColorSlots(row) {
  const slots = resolveListingColorSlotsRaw(row);
  const picked = row?.[LISTING_PUBLISH_COLOR_INDEXES_FIELD];
  if (!Array.isArray(picked) || !Array.isArray(slots) || slots.length === 0) return slots;
  const allow = new Set(
    picked
      .map((index) => Number(index))
      .filter((index) => Number.isInteger(index) && index >= 0),
  );
  // 空选择视为「不刊登任何颜色」，由上游给出明确提示，绝不回退成全色
  return slots.filter((_, index) => allow.has(index));
}

/**
 * 刊登/校验用色槽：与 UI 一致读 preferred，并规范化字段。
 * @returns {Array<{ patternName: string, mainUrl: string, mainName: string, colorCode: string, colorLabel: string, detailUrls: string[], detailNames: string[] }>}
 */
export function normalizeListingColorSlotsForPublish(listingItem) {
  return resolveListingColorSlots(listingItem)
    .map((slot) => ({
      patternName: String(slot?.patternName || slot?.mainName || '').trim(),
      mainUrl: String(slot?.mainUrl || slot?.main_url || '').trim(),
      mainName: String(slot?.mainName || slot?.patternName || slot?.main_name || '').trim(),
      colorCode: String(slot?.colorCode || slot?.color_code || '').trim(),
      colorLabel: String(slot?.colorLabel || slot?.color_label || '').trim(),
      detailUrls: detailUrlsFromListingColorSlot(slot),
      detailNames: Array.isArray(slot?.detailNames)
        ? slot.detailNames.map((n) => String(n || '').trim())
        : (Array.isArray(slot?.detail_names)
          ? slot.detail_names.map((n) => String(n || '').trim())
          : []),
    }))
    .filter((slot) => slot.mainUrl);
}

export function clearPlatformListingProductMetaForLogout() {
  userLocal.dropLegacyKeys(STORAGE_KEY);
}

/**
 * 颜色槽的身份键（用于「只刊登部分颜色」在重新刊登时沿用）：
 * 优先用编码，没有编码时用中文颜色名；避免依赖槽位下标。
 */
function listingColorSlotIdentity(slot) {
  const code = String(slot?.colorCode || slot?.color_code || '').trim().toUpperCase();
  if (code) return `code:${code}`;
  const label = String(slot?.colorLabel || slot?.color_label || '').trim();
  return label ? `label:${label}` : '';
}

/** 本次实际只刊登部分颜色时，取出这批颜色的身份键（全色刊登返回 null）。 */
export function buildListingPublishColorKeys(row) {
  const picked = row?.[LISTING_PUBLISH_COLOR_INDEXES_FIELD];
  if (!Array.isArray(picked) || picked.length === 0) return null;
  const keys = resolveListingColorSlots(row)
    .map(listingColorSlotIdentity)
    .filter(Boolean);
  return keys.length > 0 ? keys : null;
}

/** 按存下的颜色身份键还原「只刊登这些颜色」；一个都匹配不上时返回原行，退回全色。 */
export function applyListingPublishColorKeys(row, keys) {
  const wanted = Array.isArray(keys) ? keys.filter(Boolean) : [];
  if (!row || wanted.length === 0) return row;
  const slots = resolveListingColorSlots(row);
  if (!Array.isArray(slots) || slots.length <= 1) return row;
  const allow = new Set(wanted);
  const indexes = slots
    .map((slot, index) => (allow.has(listingColorSlotIdentity(slot)) ? index : -1))
    .filter((index) => index >= 0);
  if (indexes.length === 0 || indexes.length >= slots.length) return row;
  return { ...row, [LISTING_PUBLISH_COLOR_INDEXES_FIELD]: indexes };
}
